import {type Query, useQuery} from '@tanstack/react-query';
import {api} from '@/libs/baseApi.client';
import type {Platform} from '../types/platforms';
import type {SocialVerificationsStatusRes, VerificationStatus} from '../types/verify';

type VerificationItem = {
  platform: Platform;
  status: VerificationStatus;
  profile_url: string;
};

export const getSocialVerificationsStatus = async (platform?: Platform): Promise<VerificationItem[]> => {
  const response = await api.get<SocialVerificationsStatusRes>('/users/social-verification/status', {
    params: {
      platform,
    },
  });
  return response.data.verifications || [];
};

/**
 * Проверяет, есть ли активные верификации, требующие поллинга
 * @param verifications - массив верификаций
 * @returns true если есть верификации со статусами 'pending' или 'in_progress'
 */
const hasActiveVerifications = (verifications: VerificationItem[] | undefined): boolean => {
  if (!verifications || verifications.length === 0) {
    return false;
  }
  return verifications.some(v => v.status === 'pending' || v.status === 'in_progress');
};

export const useGetSocialVerificationsStatus = ({
  refetchInterval = 10000,
  platform,
  enabled,
}: {
  refetchInterval?: number;
  platform?: Platform;
  enabled?: boolean;
} = {}) => {
  return useQuery<VerificationItem[]>({
    queryKey: ['social-verifications-status', platform],
    queryFn: () => getSocialVerificationsStatus(platform),
    refetchInterval: (query: Query<VerificationItem[], Error>) => {
      // Если enabled === false, не поллим
      if (enabled === false) {
        return false;
      }

      // Проверяем данные из query для определения необходимости поллинга
      const verifications = query.state.data;
      const shouldPoll = hasActiveVerifications(verifications);

      // Если есть активные верификации, продолжаем поллинг
      // Если нет - останавливаем (возвращаем false)
      return shouldPoll ? refetchInterval : false;
    },
    enabled: enabled,
  });
};
